export const CALENDLY_URL = 'https://calendly.com/eric-blanvillain/consulting-point'
export const LINKEDIN_URL = 'https://www.linkedin.com/in/ericblanvillain'
export const GITHUB_URL = 'https://github.com/EricBlanvillain'
export const EMAIL = 'contact@eb9n.com'
export const EMAIL_URL = 'mailto:contact@eb9n.com'

export const credibilityLine = '6 years consulting · Banking, insurance, industry · ESSEC & CentraleSupélec'

export const sectors = [
  'Retail banking',
  'Asset servicing',
  'Insurance',
  'Industrial operations',
]

export const services = [
  {
    title: 'Data governance that survives contact with the business',
    body: 'Named owners per domain, quality rules written down, and a lineage view that holds up in an audit. Reviewed every month, not once a year.',
  },
  {
    title: 'Pipelines and platforms, specified and shipped',
    body: 'Target architecture, source mapping and build specs your engineers can pick up on day one. I stay through first release and handover.',
  },
  {
    title: 'AI projects that pass the finance and risk gate',
    body: 'Cost per use case, model risk documentation, and a two-page go or no-go memo. Written for the investment committee, not for a demo.',
  },
]

export const assessmentSteps = [
  {
    number: '01',
    label: 'What we cover',
    body: 'Five lenses in 30 minutes: data and AI maturity, use case shortlist, roadmap, technical feasibility, data architecture and governance. You pick the two that matter most. We move fast through the rest.',
  },
  {
    number: '02',
    label: 'What you leave with',
    body: 'Three use cases ranked, the two constraints most likely to stall them, and a 90-day sequence with owners. Written up and sent back to you within 48 hours.',
  },
  {
    number: '03',
    label: 'What it costs',
    body: 'Nothing. No deck, no pitch, no follow-up sequence. If there is work worth doing together, we talk about that separately.',
  },
]

export type Lens = {
  number: string
  title: string
  body: string
  detail: string
  questions: string[]
  leaveWith: string
}

export const lenses: Lens[] = [
  {
    number: '01',
    title: 'AI & Data maturity',
    body: 'Where you actually stand — data, platforms, skills, governance — against what your roadmap assumes.',
    detail:
      'A short diagnostic of capability versus ambition. We look at how data is owned and used today, how AI work is staffed, and whether the operating rhythm can support anything beyond a pilot.',
    questions: [
      'What is already in production, versus slideware?',
      'Who owns data quality, access, and the last mile into the business?',
      'What skills exist in-house, and where does delivery still sit with vendors?',
    ],
    leaveWith: 'A plain-language maturity read: what is real, what is assumed, and what is missing.',
  },
  {
    number: '02',
    title: 'Use case opportunities',
    body: 'The shortlist worth funding, ranked by business value and by what your organisation can absorb.',
    detail:
      'We do not walk a catalogue of demos. We start from a business problem (finance, risk, operations) and test whether AI or better data work is actually the lever.',
    questions: [
      'Where is the value: cost, risk, speed, or revenue?',
      'Do you have the data, or would the use case stall on collection?',
      'Can the organisation absorb another initiative this quarter?',
    ],
    leaveWith: 'A ranked shortlist: fund, park, or kill — with a reason a sponsor can repeat.',
  },
  {
    number: '03',
    title: 'Strategic planning & roadmap',
    body: 'A sequence with owners and decision points, not a wish list of pilots competing for the same team.',
    detail:
      'Ambition becomes a sequence. What to prove in the next 90 days, what waits on foundations, and which decision belongs to which owner.',
    questions: [
      'What has to be true before the next investment?',
      'Which workstreams are competing for the same people?',
      'What does leadership need to decide, versus what delivery can just run?',
    ],
    leaveWith: 'A first-cut roadmap: sequence, owners, and the decision points that keep it honest.',
  },
  {
    number: '04',
    title: 'Technical feasibility',
    body: 'What is buildable now, what needs foundations first, and where the effort is being underestimated.',
    detail:
      'A feasibility cut against the stack you have: platforms, models, integration, and the difference between a prototype and something operations will run.',
    questions: [
      'Can this land on current systems, or does it need architecture work first?',
      'Where is effort being underestimated: data, integration, or change?',
      'What would a credible first release actually look like?',
    ],
    leaveWith: 'A buildable / not-yet / overstated map, without a vendor pitch.',
  },
  {
    number: '05',
    title: 'Data, architecture & governance',
    body: 'Lineage, quality, access and controls — the part that decides whether anything reaches production.',
    detail:
      'In financial services this is often the binding constraint. We look at whether data can be trusted, who can use it, and whether controls will let a use case survive risk and audit.',
    questions: [
      'Can you explain where a number came from?',
      'Are access, quality, and lineage good enough for production?',
      'What will risk, compliance, or audit block if you ignore it now?',
    ],
    leaveWith: 'The governance and architecture gaps that decide if a use case can actually ship.',
  },
]

export const audience = [
  'CIOs setting the AI agenda across a regulated estate',
  'CDOs turning data foundations into funded outcomes',
  'Heads of data weighing feasibility against delivery capacity',
  'Transformation leads who need finance, risk and ops aligned',
]

export const proof = [
  {
    label: 'Both sides',
    title: 'Business and technical',
    body: 'I translate between the people who build models and the people who sign off on budget, risk and controls.',
  },
  {
    label: 'Sector',
    title: 'Financial services',
    body: 'Work shaped by the constraints that matter in FS: regulation, legacy platforms, model risk, and audit trails.',
  },
  {
    label: 'Practice',
    title: 'Consulting that ships',
    body: 'Currently Business Consultant, Data & AI at Onepoint. Previously Square Management and BEAM Advisory.',
  },
  {
    label: 'Grounding',
    title: 'International, based in Paris',
    body: 'ESSEC IBEA across four continents. Le Wagon, CentraleSupélec × OpenClassrooms and DataScientest on the technical side.',
  },
]

export const githubChips = [
  'brand_navigator',
  'market_intelligence',
  'agent_orchestrator',
  'mistral_finetuning',
]
